import { default as moment } from "moment";

import { ButtonVariant, ButtonView } from "common/views/button/Button.view";
import MutationButton from "common/views/button/MutationButton";
import IconView from "common/views/icon/Icon.view";
import { ImageVariant, ImageView } from "common/views/image/Image.view";
import PageLinkView from "common/views/pageLink/PageLink.view";
import { IDataFragment } from "core/components/FragmentProvider";
import UrlUtil from "core/util/UrlUtil";
import CatalogLabels from "panels/catalog/Catalog.labels";
import Course, { Variant } from "panels/catalog/entities/Course";
import ICourseFragmentProps from "./ICourseFragmentProps";
import { StyledNewBanner, StyledThumbnailOverlay } from "./styled";
import TokenSession from "core/util/session/TokenSession";
import SubscriptionExtend from "panels/catalog/entities/SubscriptionExtend";
import CourseBookmark from "panels/catalog/entities/CourseBookmark";
import React, { useEffect, useState } from "react";
import Label from "core/components/label/Label";
import { IBreadcrumb } from "core/util/IBreadcrumb";
import { lastItem } from "core/util/ArrayUtil";
import { DialogView } from "common/views/dialog/Dialog.view";
import RenderVariants from "../renderVariants";
import ExtendSubscription from "./extendSubscription";
import { BlockView, BlockVariant } from "common/views/block/BlockView.view";
import { RateItem } from "panels/catalog/entities/Rating";
import Rating from "react-rating";
import { MutationType } from "typegraph";
import TypographyView from "common/views/typographyView/Typography.view";


export const subsciptionExpired: IDataFragment<ICourseFragmentProps> = {
    name: "Subscription Expired",
    label: { k: "course.item.subscription", v: "Subscription Expired" },
    component: ({ course, detailsLinkVisible }) => {
        if (!course) {
            return null;
        }
        if (course && course.subscriptionEndDate && !course.canExtend && !course.activeSubscriptionExists) {
            return (<TypographyView viewKey="subscriptionexpire.title">
                <CatalogLabels.Labels.subscriptionExpired />
            </TypographyView>)
        }
        return null;
    }
};


// launch course / primary action
export const launchCourse: IDataFragment<ICourseFragmentProps> = {
    name: "Launch Course",
    label: { k: "course.item.launchCourse", v: "Launch" },
    enabled: ({ course, detailsLinkVisible }) =>
        !(course && course.baseType === "base:program" && detailsLinkVisible),
    component: ({ course, actions, launchable }) => {
        if (!course) {
            return <ButtonView variant={ButtonVariant.PRIMARY} viewKey="catalog.launchCourse">{'...'}</ButtonView>;
        }

        if (launchable === false) {
            return null;
        }

        if (course && course.subscriptionEndDate && !course.canExtend && !course.activeSubscriptionExists) {
            return null;
        }

        // launchable
        if (course.canLaunch) {
            return (
                <ButtonView
                    variant={ButtonVariant.PRIMARY}
                    viewKey="catalog.launchCourse"
                    onClick={() => actions.launchCourse(course)}
                    attrs={course && {
                        // TODO move this
                        'data-wap_type': 'pt-course-launch',
                        'data-wap_ref': `${course.itemExternalId}:${course.title}`
                    }}
                >
                    <CatalogLabels.Labels.LaunchCourse />
                </ButtonView>
            );
        }

        // can't launch, but still free to enroll
        if (!course.completion && course.variants && (!course.canBuy && course.variants.find((v) => v.price === 0 && !v.displayOnly))) {
            if (TokenSession.isAuthenticated()) {
                return (
                    <MutationButton
                        variant={ButtonVariant.PRIMARY}
                        viewKey="catalog.enroll"
                        clazz={Course}
                        params={{ id: course.metaId }}
                        clearCacheOnSuccess={true}
                    >
                        <CatalogLabels.Labels.EnrollCourse />
                    </MutationButton>
                );
            }
            return (
                <ButtonView
                    variant={ButtonVariant.PRIMARY}
                    viewKey="catalog.enroll"
                    onClick={() => actions.enrollInCourse(course)}
                >
                    <CatalogLabels.Labels.EnrollCourse />
                </ButtonView>
            );
        }

        return null;
    }
};

export const detailsButton: IDataFragment<ICourseFragmentProps> = {
    name: "Course Details Button",
    label: { k: "course.item.detailsButton", v: "Details" },
    component: ({ course, detailsLinkVisible }) => {
        if (detailsLinkVisible === false) {
            return false;
        }
        /**Finding  if variants  price = 0 then show only view details  button*/
        let variantList = [];
        if (course && course.variants && course.variants.length > 0) {
            variantList = course.variants.filter(variant => variant.price > 0);
        }
        if (course && course.baseType === "base:program" && !course.canBuy) {
            return (
                <PageLinkView
                    attrs={
                        course && {
                            "data-wap_ref": `${course.itemExternalId}:${course.title
                                }`
                        }
                    }
                    params={{
                        entryId: course && course.id,
                        subscribed:
                            course &&
                            (course.activeSubscriptionExists ||
                                course.canExtend),
                        itemId: course && course.itemId
                    }}
                    viewKey="catalog.detailsLink.link"
                    breadcrumbs={(target, breadcrumbs) => {
                        let nextCrumbs: IBreadcrumb[] = [];
                        const courseKey = "course:" + course.id;
                        // push current page's crumb
                        const lastCrumb = lastItem(breadcrumbs);

                        const currentPath = UrlUtil.getCurrentPath();
                        // if last crumb was for the current page and has a target, use that one
                        if (
                            lastCrumb &&
                            lastCrumb.target.pathname === currentPath
                        ) {
                            nextCrumbs = [
                                ...breadcrumbs.slice(0, breadcrumbs.length - 1),
                                {
                                    ...lastCrumb,
                                    target: {
                                        pathname: currentPath,
                                        search: UrlUtil.getSearch()
                                    }
                                }
                            ];
                        } else if (
                            // if last crumb was for the current page and has no target path associated
                            lastCrumb &&
                            lastCrumb.target.pathname === ""
                        ) {
                            nextCrumbs = [
                                ...breadcrumbs.slice(0, breadcrumbs.length - 1),
                                {
                                    ...lastCrumb,
                                    target: {
                                        pathname: currentPath,
                                        search: UrlUtil.getSearch()
                                    }
                                }
                            ];
                        } else {
                            // else create a new one for the current page
                            nextCrumbs = [
                                ...breadcrumbs,
                                {
                                    key: "course-detail",
                                    label: {
                                        k: courseKey,
                                        v:
                                            window.parent.location.pathname.slice(
                                                1
                                            ) || "Home"
                                    },
                                    target: {
                                        pathname: currentPath,
                                        search: UrlUtil.getSearch()
                                    }
                                }
                            ];
                        }

                        // push article crumb
                        nextCrumbs.push({
                            key: courseKey,
                            label: {
                                k: courseKey,
                                v: course.title
                            },
                            //target
                            target: {
                                pathname: "",
                                search: ""
                            }
                        });

                        return nextCrumbs;
                    }}
                >
                    <ButtonView
                        variant={ButtonVariant.PRIMARY}
                        viewKey="catalog.detailsButton.button"
                    >
                        <CatalogLabels.Labels.ViewDetails />
                    </ButtonView>
                </PageLinkView>
            );
        }
        return null;
    }
};

// view details
export const detailsLink: IDataFragment<ICourseFragmentProps> = {
    name: "Course Details Link",
    label: { k: "course.item.detailsLink", v: "Details" },
    enabled: ({ course, detailsLinkVisible }) =>
        !(course && course.baseType === "base:program" && detailsLinkVisible && !course.canBuy),
    component: ({ course, detailsLinkVisible }) => {
        if (detailsLinkVisible === false) {
            return false;
        }
        return (
            <PageLinkView
                attrs={course && {
                    'data-wap_ref': `${course.itemExternalId}:${course.title}`
                }}
                params={{ entryId: course && course.id, subscribed: course && (course.activeSubscriptionExists || course.canExtend), itemId: course && course.itemId }}
                viewKey="catalog.detailsLink.link"
                breadcrumbs={(target, breadcrumbs) => {
                    let nextCrumbs: IBreadcrumb[] = [];
                    const courseKey = "course:" + course.id;
                    // push current page's crumb
                    const lastCrumb = lastItem(breadcrumbs);

                    const currentPath = UrlUtil.getCurrentPath();
                    // if last crumb was for the current page and has a target, use that one
                    if (
                        lastCrumb &&
                        lastCrumb.target.pathname === currentPath
                    ) {
                        nextCrumbs = [
                            ...breadcrumbs.slice(0, breadcrumbs.length - 1),
                            {
                                ...lastCrumb,
                                target: {
                                    pathname: currentPath,
                                    search: UrlUtil.getSearch()
                                }
                            }
                        ];
                    } else if ( // if last crumb was for the current page and has no target path associated
                        lastCrumb &&
                        lastCrumb.target.pathname === ''
                    ) {
                        nextCrumbs = [
                            ...breadcrumbs.slice(0, breadcrumbs.length - 1),
                            {
                                ...lastCrumb,
                                target: {
                                    pathname: currentPath,
                                    search: UrlUtil.getSearch()
                                }
                            }
                        ];
                    } else {
                        // else create a new one for the current page
                        nextCrumbs = [
                            ...breadcrumbs,
                            {
                                key: "course-detail",
                                label: {
                                    k: courseKey,
                                    v: window.parent.location.pathname.slice(1) || "Home"
                                },
                                target: {
                                    pathname: currentPath,
                                    search: UrlUtil.getSearch()
                                }
                            },
                        ];
                    }

                    // push article crumb
                    nextCrumbs.push({
                        key: courseKey,
                        label: {
                            k: courseKey,
                            v: course.title,
                        },
                        //target
                        target: {
                            pathname: '',
                            search: ''
                        }
                    });

                    return nextCrumbs;
                }}
            >
                <ButtonView variant={ButtonVariant.SECONDARY} viewKey="catalog.detailsLink.button">
                    <CatalogLabels.Labels.ViewDetails />
                </ButtonView>
            </PageLinkView>
        );
    }
};

// clickable image
export const actionImage: IDataFragment<ICourseFragmentProps> = {
    name: "Action Image",
    label: { k: "course.item.actionImage", v: "Image" },
    component: ({ course, actions, showNew, launchable }) => {
        return (
            <div style={{ position: "relative" }}>
                <ImageView
                    src={course && course.iconUrl}
                    overlay={course && course.productType && {
                        src: course.productType.iconUrl,
                        position: "top left"
                    }}
                    variant={ImageVariant.THUMBNAIL}
                    viewKey="course.image.actions"
                />
                {
                    (showNew && course && course.startDate && moment(new Date()).diff(course.startDate, 'days') < 30) && (
                        <StyledNewBanner>
                            <CatalogLabels.Labels.New />
                        </StyledNewBanner>
                    )
                }
                {course && course.launchURL && launchable && (
                    <StyledThumbnailOverlay onClick={() => actions.launchCourse(course)}
                        attrs={course && {
                            // TODO move this
                            'data-wap_type': 'pt-course-launch',
                            'data-wap_ref': `${course.itemExternalId}:${course.title}`
                        }}>
                        <IconView icon="play" viewKey="course-play-icon" />
                    </StyledThumbnailOverlay>
                )}
            </div>
        );
    }
};

// vote up

// favorite

// add to cart
export const buy: IDataFragment<ICourseFragmentProps> = {
    name: "Buy",
    label: { k: "course.item.buy", v: "Purchase" },
    enabled: ({ course }) => !course || (course && course.canBuy), // skeleton when loading, hide if loaded and not buyable
    component: ({ course, actions, disableDirectBuy, loginBeforeBuy }) => {
        const [isOpen, toggleOpen] = useState(false);
        const [variants, setVariants] = useState([]);


        const _onclickHandler = () => {
            if (variants.length === 1)
                actions.addToCart(course, variants[0], disableDirectBuy, loginBeforeBuy);
            else
                toggleOpen(true);
        }

        useEffect(() => {
            //here filtering variants
            if (course && course.variants && course.variants.length > 0) {
                const variantList = course.variants.filter(variant => !variant.repurchaseVariant && (course.canBuy || variant.price > 0) && !variant.displayOnly)
                setVariants(variantList);
            }

        }, [course])

        return (
            course && variants && variants.length > 0 &&
            <React.Fragment>
                <DialogView isOpen={isOpen} viewKey="dialog" onClosed={() => toggleOpen(!isOpen)}>
                    <RenderVariants variants={variants}
                        addTocart={(v: Variant) => actions.addToCart(course, v, disableDirectBuy, loginBeforeBuy)}
                        toggleOpen={() => toggleOpen(!isOpen)}
                        isRepurchase={false} />
                </DialogView>
                <ButtonView
                    variant={ButtonVariant.PRIMARY}
                    viewKey="catalog.buyCourse"
                    onClick={() => _onclickHandler()}
                >
                    {variants.length == 1 ?
                        (<CatalogLabels.Labels.BuyCourse args={{ price: variants[0].price, title: variants[0].title }} />)
                        : <p>Add to Cart</p>
                    }
                </ButtonView>
            </React.Fragment>
        )


    }
};

// checkout

// add to calendar
export const addToCalendar: IDataFragment<ICourseFragmentProps> = {
    name: "Add to Calendar",
    label: { k: "course.item.addToCalendar", v: "Add to Calendar" },
    component: ({ course, actions, label }) => (
        <PageLinkView
            viewKey="catalog.detailsLink.link"
            render={({ path }) => {

                return (
                    <ButtonView variant={ButtonVariant.PRIMARY} viewKey="catalog.addToCalendar" onClick={() => actions.addToCalendar(course, path)}>
                        <CatalogLabels.Labels.AddToCalendar />
                    </ButtonView>
                );
            }}
        />
    )
};

// more info
export const moreInfo: IDataFragment<ICourseFragmentProps> = {
    name: "More Info",
    label: { k: "course.item.moreInfo", v: "More Info" },
    component: ({ course }) => {
        return (
            course && course.infoUrl && !course.canLaunch &&
            <a href={course.infoUrl} target="_blank" rel="noopener noreferrer">
                <ButtonView variant={ButtonVariant.PRIMARY} viewKey="catalog.moreInfoLink">
                    <CatalogLabels.Labels.MoreInfo />
                </ButtonView>
            </a>
        );
    }
};

// intel add to calendar
export const intelAddToCalendar: IDataFragment<ICourseFragmentProps> = {
    name: "Intel Add to Calendar",
    label: { k: "course.item.intel.addToCalendar", v: "Add to Calendar" },
    component: ({ course, actions, label }) => (
        <PageLinkView
            viewKey="catalog.detailsLink.link"
            render={({ path }) => {

                return (
                    <ButtonView variant={ButtonVariant.PRIMARY}
                        viewKey="catalog.addToCalendar"
                        onClick={() => actions.addToCalendar(course, path)}
                        attrs={course && {
                            'data-wap_type': 'pt-add-to-calender',
                            'data-wap_ref': `${course.itemExternalId}:${course.title}`,
                            'data-wap': JSON.stringify({
                                'user_id': TokenSession.getUserId(),
                                'email_id': TokenSession.getUser().email,
                                'course_id': course.itemExternalId,
                                'course_name': course.title,
                                'start_date': course.startDate
                            })
                        }}>
                        <CatalogLabels.Labels.AddToCalendar />
                    </ButtonView>
                );
            }}
        />
    )
};

export const extendSubscription: IDataFragment<ICourseFragmentProps> = {
    name: "Extend Subscription",
    label: { k: "subscription.extend", v: "Extend Subscription" },
    enabled: ({ course }) => course && course.canExtend && course.canExtend != undefined ? course.canExtend : false,
    component: ({ course, actions, disableDirectBuy }) => (<ExtendSubscription course={course} actions={actions} disableDirectBuy={disableDirectBuy} />)

};

// add to calendar
export const addToBookmark: IDataFragment<ICourseFragmentProps> = {
    name: "Add to Bookmark",
    label: { k: "course.item.addToBookmark", v: "Add to Bookmark List" },
    component: ({ course }) => {
        var msg = "+Add To List";
        if (course && course.bookmarked) {
            msg = "-Remove from List";
        }
        if (!course) {
            return <ButtonView variant={ButtonVariant.PRIMARY} viewKey="course.addToBookmark">{'...'}</ButtonView>;
        }
        return (
            <MutationButton
                variant={ButtonVariant.MINIMAL}
                viewKey="course.addToBookmark"
                clazz={CourseBookmark}
                params={{ id: Number(course.metaId) }}
                clearCacheOnSuccess={true}>
                <CatalogLabels.Labels.CourseBookmark args={{ title: msg }} />
            </MutationButton>
        );
    }

};


export const userRating: IDataFragment<ICourseFragmentProps> = {
    name: "User Rating",
    label: { k: "course.item.user.rating", v: "User Rating" },

    component: ({ course }) => {
        const [userItemRating, setUserItemRating] = useState(course.userItemRating)
        const handleClick = (value) => {
            setUserItemRating(value);
        }

        return (
            <React.Fragment>
                <BlockView variant={BlockVariant.PAGING} viewKey="userrating">
                    <Label k="course.userRating" v="User rating: " />
                    <MutationButton
                        viewKey="course.userrating.button"
                        clazz={RateItem}
                        params={{ itemId: course.itemId, rating: userItemRating }}
                        method={MutationType.UPDATE}
                        clearCacheOnSuccess={true}
                    >
                        <Rating
                            emptySymbol="far fa-star fa-1x medium"
                            fullSymbol="fas fa-star fa-1x medium"
                            fractions={2}
                            initialRating={userItemRating}
                            direction='ltr'
                            onClick={(value) => { handleClick(value) }}
                        />
                    </MutationButton>
                </BlockView>
            </React.Fragment>
        )
    }
};


// WEBPACK FOOTER //
// ./src/panels/catalog/fragments/course/courseItemActionFragments.tsx